/**
 * Utility functions for creating URL-friendly slugs
 */

/**
 * Convert a string to a URL-friendly slug
 * @param text - Text to convert to slug
 * @returns URL-friendly slug
 */
export function createSlug(text: string): string {
  if (!text) return '';
  
  return text
    .toLowerCase()
    .trim()
    // Replace spaces and forward slashes with hyphens
    .replace(/[\s/]+/g, '-')
    // Replace special characters with hyphens
    .replace(/[^\w\-]+/g, '-')
    // Remove multiple consecutive hyphens
    .replace(/-+/g, '-')
    // Remove hyphens from start and end
    .replace(/^-+|-+$/g, '');
}

/**
 * Convert a slug back to a readable format for display
 * @param slug - Slug to convert back
 * @returns Readable text
 */
export function slugToText(slug: string): string {
  if (!slug) return '';
  
  return slug
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * Create a sport URL slug from sport name
 * @param sportName - Sport name (e.g., "Running/Athletics")
 * @returns URL-friendly sport slug (e.g., "running-athletics")
 */
export function createSportSlug(sportName: string): string {
  return createSlug(sportName);
}

/**
 * Create a category URL slug from category name
 * @param categoryName - Category name
 * @returns URL-friendly category slug
 */
export function createCategorySlug(categoryName: string): string {
  return createSlug(categoryName);
}

/**
 * Normaliza los alias históricos del slug de deporte a su forma canónica.
 *
 * `running-athletics` y `running-atletismo` sirven EXACTAMENTE el mismo listado que `running`
 * (vienen de la grafía sucia "Running/Atletismo" en BD). Las tres URLs son alcanzables, así que
 * la canónica y el hreflang de /sport deben construirse con el resultado de esta función: si
 * cada alias se auto-referencia, compiten entre sí por el mismo contenido.
 *
 * @param sportCode - Slug de deporte tal y como llega en la URL
 * @returns Slug canónico del deporte
 */
export function normalizeSportCode(sportCode: string): string {
  return sportCode === 'running-athletics' || sportCode === 'running-atletismo'
    ? 'running'
    : sportCode;
}